/* Noll Drop theme — drawers, policy pop-ups, gallery, variant picker, AJAX cart. No dependencies. */
(() => {
  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const routes = window.routes || { root: '/', cartAdd: '/cart/add', cartChange: '/cart/change', cart: '/cart' };
  const CART_SECTION = 'cart-drawer';
  const GENERIC_ERROR = 'Something went wrong. Please try again.';
  const readJson = async (res) => {
    try { return await res.json(); } catch { return {}; }
  };

  /* ---------------- Drawers (menu + cart) ---------------- */
  let lastFocus = null;

  function openDrawer(name) {
    const drawer = qs(`[data-drawer="${name}"]`);
    if (!drawer) return false;
    qsa('.drawer.is-open').forEach((d) => d !== drawer && closeDrawer(d, false));
    lastFocus = document.activeElement;
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    document.documentElement.classList.add('is-locked');
    qsa(`[data-drawer-open="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const focusTarget = qs('.drawer__panel [data-drawer-close]', drawer);
    if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 50);
    return true;
  }

  function closeDrawer(drawer, restoreFocus = true) {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    qsa(`[data-drawer-open="${drawer.dataset.drawer}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (!qs('.drawer.is-open')) document.documentElement.classList.remove('is-locked');
    if (restoreFocus && lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      if (openDrawer(opener.dataset.drawerOpen)) e.preventDefault();
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      closeDrawer(closer.closest('.drawer'));
      return;
    }
    const menuLink = e.target.closest('[data-menu-link]');
    if (menuLink) {
      // Close first so the page can scroll, then let the hash link do its job.
      closeDrawer(menuLink.closest('.drawer'), false);
      return;
    }

    /* Policy pop-ups */
    const policyOpen = e.target.closest('[data-policy-open]');
    if (policyOpen) {
      const dialog = document.getElementById(policyOpen.dataset.policyOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        e.preventDefault();
        dialog.showModal();
      }
      return;
    }
    const policyClose = e.target.closest('[data-policy-close]');
    if (policyClose) {
      policyClose.closest('dialog').close();
      return;
    }
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('policy')) e.target.close();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !qs('dialog[open]')) qsa('.drawer.is-open').forEach((d) => closeDrawer(d));
  });

  /* ---------------- Cart ---------------- */
  function renderCart(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = qs('[data-cart]', doc);
    const current = qs('#CartDrawer [data-cart]');
    if (fresh && current) current.replaceWith(fresh);
    if (fresh) qsa('[data-cart-count]').forEach((el) => (el.textContent = fresh.dataset.count));
  }

  async function refreshCart() {
    const res = await fetch(`${window.location.pathname}?sections=${CART_SECTION}`);
    const json = await readJson(res);
    renderCart(json[CART_SECTION]);
  }

  async function addToCart(form, button, errorEl) {
    if (errorEl) errorEl.hidden = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      const body = new FormData(form);
      body.append('sections', CART_SECTION);
      body.append('sections_url', window.location.pathname);
      const res = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const data = await readJson(res);
      if (!res.ok || data.status) throw new Error(data.description || data.message || GENERIC_ERROR);
      if (data.sections && data.sections[CART_SECTION]) renderCart(data.sections[CART_SECTION]);
      else await refreshCart();
      openDrawer('cart');
    } catch (err) {
      if (errorEl) {
        errorEl.textContent = err instanceof SyntaxError ? GENERIC_ERROR : err.message;
        errorEl.hidden = false;
      }
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  async function changeLine(cartEl, line, quantity) {
    cartEl.classList.add('is-busy');
    try {
      const res = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: [CART_SECTION], sections_url: window.location.pathname })
      });
      const data = await readJson(res);
      if (!res.ok || data.status) throw new Error(data.description || data.message || GENERIC_ERROR);
      if (!cartEl.closest('#CartDrawer')) return window.location.reload();
      if (data.sections && data.sections[CART_SECTION]) renderCart(data.sections[CART_SECTION]);
      else await refreshCart();
    } catch (err) {
      const errorEl = qs('[data-cart-error]', cartEl);
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      }
      cartEl.classList.remove('is-busy');
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cart-qty]');
    if (!btn) return;
    e.preventDefault();
    const cartEl = btn.closest('[data-cart]');
    const line = Number(btn.closest('[data-line]').dataset.line);
    changeLine(cartEl, line, Math.max(0, Number(btn.dataset.cartQty)));
  });

  /* ---------------- Product: variant picker + add to cart ---------------- */
  const splitValue = (value) => {
    const i = value.indexOf(' for ');
    return i === -1 ? [value, ''] : [value.slice(0, i), value.slice(i + 5)];
  };
  const optionLabel = (value) => {
    const [short, rest] = splitValue(value);
    return rest ? `${short} (${rest})` : short;
  };

  function initProduct(root) {
    const dataEl = qs('[data-product-json]', root);
    const form = qs('form[data-product-form]', root);
    if (!dataEl || !form) return;
    const { variants } = JSON.parse(dataEl.textContent);
    const idInput = qs('[data-variant-input]', form);
    const groups = qsa('[data-option-index]', root);
    const atc = qs('[data-atc]', root);
    const atcLabel = atc && qs('[data-atc-label]', atc);
    const atcError = qs('[data-atc-error]', root);
    const priceEls = qsa('[data-price]', root);
    const updateUrl = root.dataset.updateUrl === 'true';

    const selection = () => {
      const sel = [];
      groups.forEach((g) => {
        const b = qs('.pdp-value.is-selected', g);
        sel[Number(g.dataset.optionIndex)] = b ? b.dataset.value : null;
      });
      return sel;
    };
    const matches = (v, sel) => v.options.every((o, i) => sel[i] == null || o === sel[i]);

    function update() {
      const sel = selection();
      const variant = variants.find((v) => v.options.every((o, i) => o === sel[i]));

      groups.forEach((g) => {
        const index = Number(g.dataset.optionIndex);
        qsa('.pdp-value', g).forEach((b) => {
          const trial = sel.slice();
          trial[index] = b.dataset.value;
          const ok = variants.some((v) => v.available && matches(v, trial));
          b.classList.toggle('is-unavailable', !ok);
        });
        const label = qs('[data-option-selected]', g);
        if (label && sel[index]) label.textContent = optionLabel(sel[index]);
      });

      if (variant) {
        idInput.value = variant.id;
        priceEls.forEach((el) => {
          el.innerHTML = (variant.compare ? `<s class="pdp-price__compare">${variant.compare}</s>` : '') + `<span>${variant.price}</span>`;
        });
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }
      if (atc) {
        const available = !!(variant && variant.available);
        atc.disabled = !available;
        atcLabel.textContent = !variant
          ? atc.dataset.labelUnavailable
          : available
            ? atc.dataset.labelAdd
            : atc.dataset.labelSoldout;
      }
      if (atcError) atcError.hidden = true;
    }

    root.addEventListener('click', (e) => {
      const valueBtn = e.target.closest('.pdp-value');
      if (valueBtn && root.contains(valueBtn)) {
        const group = valueBtn.closest('[data-option-index]');
        qsa('.pdp-value', group).forEach((b) => {
          const on = b === valueBtn;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        update();
        return;
      }
      const guide = e.target.closest('[data-size-guide-open]');
      if (guide) {
        const details = qs('details[data-size-guide]', root);
        if (details) {
          details.open = true;
          details.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
          qs('summary', details).focus({ preventScroll: true });
        }
      }
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!atc || atc.disabled || atc.classList.contains('is-loading')) return;
      addToCart(form, atc, atcError);
    });

    update();
  }

  /* ---------------- Gallery ---------------- */
  function initGallery(gallery) {
    const track = qs('[data-gallery-track]', gallery);
    const slides = qsa('.gallery__slide', gallery);
    if (!track || slides.length < 2) return;
    const current = qs('[data-gallery-current]', gallery);
    const dots = qsa('.gallery__dot', gallery);
    const thumbs = qsa('.gallery__thumb', gallery);
    const thumbTrack = qs('[data-thumb-track]', gallery);
    let index = 0;
    let ticking = false;

    function setActive(i) {
      if (i === index && current && current.textContent === String(i + 1)) return;
      index = i;
      if (current) current.textContent = String(i + 1);
      dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      thumbs.forEach((t, n) => {
        t.classList.toggle('is-active', n === i);
        if (n === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      const thumb = thumbs[i];
      if (thumb && thumbTrack) {
        const left = thumb.offsetLeft - (thumbTrack.clientWidth - thumb.clientWidth) / 2;
        thumbTrack.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      slides.forEach((s, n) => {
        const video = qs('video', s);
        if (video) n === i ? video.play().catch(() => {}) : video.pause();
      });
    }

    function go(i) {
      const n = slides.length;
      const target = ((i % n) + n) % n;
      track.scrollTo({ left: target * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      setActive(target);
    }

    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const i = Math.round(track.scrollLeft / track.clientWidth);
          if (i !== index) setActive(Math.max(0, Math.min(slides.length - 1, i)));
        });
      },
      { passive: true }
    );

    gallery.addEventListener('click', (e) => {
      if (e.target.closest('[data-gallery-prev]')) return go(index - 1);
      if (e.target.closest('[data-gallery-next]')) return go(index + 1);
      const jump = e.target.closest('[data-gallery-go]');
      if (jump) go(Number(jump.dataset.galleryGo));
    });

    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
    });

    window.addEventListener('resize', () => {
      track.scrollTo({ left: index * track.clientWidth, behavior: 'auto' });
    });
  }

  function init(root = document) {
    qsa('[data-gallery]', root).forEach(initGallery);
    qsa('[data-product-section]', root).forEach(initProduct);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor: re-init a section after it is re-rendered.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
